import { BaseApi } from './base.api.js';

export class IntegrationConnectionApi extends BaseApi {
    constructor(client, basePath = '/integration-connections') {
        super(client, basePath);
    }

    async listProviders() {
        const response = await this.client.get(`${this.basePath}/providers`);
        return response.data;
    }

    async test(id) {
        const response = await this.client.post(`${this.basePath}/${id}/test`);
        return response.data;
    }

    async listEndpoints(id) {
        const response = await this.client.get(`${this.basePath}/${id}/endpoints`);
        return response.data;
    }

    async createEndpoint(id, data) {
        const response = await this.client.post(`${this.basePath}/${id}/endpoints`, data);
        return response.data;
    }

    async updateEndpoint(id, endpointId, data) {
        const response = await this.client.put(`${this.basePath}/${id}/endpoints/${endpointId}`, data);
        return response.data;
    }

    async deleteEndpoint(id, endpointId) {
        const response = await this.client.delete(`${this.basePath}/${id}/endpoints/${endpointId}`);
        return response.data;
    }

    async invokeEndpoint(id, endpointKey, body = {}, query = {}) {
        const response = await this.client.post(
            `${this.basePath}/${id}/endpoints/${encodeURIComponent(endpointKey)}/invoke`,
            body,
            { params: query },
        );
        return response.data;
    }

    async listInvocations(id, params = {}) {
        const response = await this.client.get(`${this.basePath}/${id}/invocations`, { params });
        return response.data;
    }

    async listProviderDefinitions(params = {}) {
        const response = await this.client.get(`${this.basePath}/provider-definitions`, { params });
        return response.data;
    }

    async createProviderDefinition(data) {
        const response = await this.client.post(`${this.basePath}/provider-definitions`, data);
        return response.data;
    }

    async updateProviderDefinition(id, data) {
        const response = await this.client.put(`${this.basePath}/provider-definitions/${id}`, data);
        return response.data;
    }

    async deleteProviderDefinition(id) {
        const response = await this.client.delete(`${this.basePath}/provider-definitions/${id}`);
        return response.data;
    }

    async listMarketplace(params = {}) {
        const response = await this.client.get(`${this.basePath}/marketplace`, { params });
        return response.data;
    }

    async listResources(id) {
        const response = await this.client.get(`${this.basePath}/${id}/resources`);
        return response.data;
    }

    async discoverResource(id, resourceKey, body = {}) {
        const response = await this.client.post(
            `${this.basePath}/${id}/resources/${encodeURIComponent(resourceKey)}/discover`,
            body,
        );
        return response.data;
    }

    async listTargetFields(id, entityType) {
        const response = await this.client.get(
            `${this.basePath}/${id}/targets/${encodeURIComponent(entityType)}/fields`,
        );
        return response.data;
    }

    async listMappings(id, params = {}) {
        const response = await this.client.get(`${this.basePath}/${id}/mappings`, { params });
        return response.data;
    }

    async createMapping(id, data) {
        const response = await this.client.post(`${this.basePath}/${id}/mappings`, data);
        return response.data;
    }

    async getMapping(id, mappingId) {
        const response = await this.client.get(`${this.basePath}/${id}/mappings/${mappingId}`);
        return response.data;
    }

    async updateMapping(id, mappingId, data) {
        const response = await this.client.put(`${this.basePath}/${id}/mappings/${mappingId}`, data);
        return response.data;
    }

    async deleteMapping(id, mappingId) {
        const response = await this.client.delete(`${this.basePath}/${id}/mappings/${mappingId}`);
        return response.data;
    }

    async cloneMapping(id, mappingId, data = {}) {
        const response = await this.client.post(`${this.basePath}/${id}/mappings/${mappingId}/clone`, data);
        return response.data;
    }

    async publishMapping(id, mappingId, data = {}) {
        const response = await this.client.post(`${this.basePath}/${id}/mappings/${mappingId}/publish`, data);
        return response.data;
    }

    async previewMapping(id, mappingId, data = {}) {
        const response = await this.client.post(`${this.basePath}/${id}/mappings/${mappingId}/preview`, data);
        return response.data;
    }

    async importMapping(id, mappingId, data) {
        const response = await this.client.post(`${this.basePath}/${id}/mappings/${mappingId}/import`, data);
        return response.data;
    }

    async listRuns(id, params = {}) {
        const response = await this.client.get(`${this.basePath}/${id}/runs`, { params });
        return response.data;
    }

    async getRun(id, runId) {
        const response = await this.client.get(`${this.basePath}/${id}/runs/${runId}`);
        return response.data;
    }

    async listRunItems(id, runId, params = {}) {
        const response = await this.client.get(`${this.basePath}/${id}/runs/${runId}/items`, { params });
        return response.data;
    }

    async replayRunFailures(id, runId, data = {}) {
        const response = await this.client.post(`${this.basePath}/${id}/runs/${runId}/replay`, data);
        return response.data;
    }

    async listSchedules(id) {
        const response = await this.client.get(`${this.basePath}/${id}/schedules`);
        return response.data;
    }

    async upsertSchedule(id, data) {
        const response = await this.client.post(`${this.basePath}/${id}/schedules`, data);
        return response.data;
    }

    async runScheduleNow(id, syncStateId) {
        const response = await this.client.post(
            `${this.basePath}/${id}/schedules/${syncStateId}/run-now`,
        );
        return response.data;
    }

    async pauseSchedule(id, syncStateId, data = {}) {
        const response = await this.client.post(
            `${this.basePath}/${id}/schedules/${syncStateId}/pause`,
            data,
        );
        return response.data;
    }

    async resetCheckpoint(id, syncStateId, data = {}) {
        const response = await this.client.post(
            `${this.basePath}/${id}/schedules/${syncStateId}/reset-checkpoint`,
            data,
        );
        return response.data;
    }

    async executeAction(id, data) {
        const response = await this.client.post(`${this.basePath}/${id}/actions/execute`, data);
        return response.data;
    }

    async resolveConflict(id, itemId, data) {
        const response = await this.client.post(
            `${this.basePath}/${id}/conflicts/${itemId}/resolve`,
            data,
        );
        return response.data;
    }

    async upgradeProvider(id, data) {
        const response = await this.client.post(`${this.basePath}/${id}/upgrade-provider`, data);
        return response.data;
    }

    async getWebhookStatus(id) {
        const response = await this.client.get(`${this.basePath}/${id}/webhook`);
        return response.data;
    }

    async enableWebhook(id) {
        const response = await this.client.post(`${this.basePath}/${id}/webhook/enable`);
        return response.data;
    }

    async rotateWebhookSecret(id) {
        const response = await this.client.post(`${this.basePath}/${id}/webhook/rotate-secret`);
        return response.data;
    }

    async disableWebhook(id) {
        const response = await this.client.delete(`${this.basePath}/${id}/webhook`);
        return response.data;
    }
}
